import { useState, useEffect } from 'react'
import { getDataAPI, postDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import DatePicker from '../components/ui/DatePicker'
import { parseLocalDate, localDateDisplay } from '../lib/dateUtils'

interface Task {
  id: string
  title: string
  description?: string
  due_date?: string
  status: string
  priority?: string
  assigned_to_user_id?: string
  category?: 'shows' | 'music' | 'commerce' | 'finance' | 'tour-manager' | 'general'
}

interface User {
  id: string
  email: string
}

export default function TasksPage() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [_isLoading, setIsLoading] = useState(true)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    assigned_to_user_id: '',
    category: 'general' as 'shows' | 'music' | 'commerce' | 'finance' | 'tour-manager' | 'general',
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tasksData, usersData] = await Promise.all([
          getDataAPI('user/tasks').catch(() => []),
          getDataAPI('users').catch(() => []),
        ])
        setTasks(tasksData?.data?.data || tasksData?.data || tasksData || [])
        setUsers(usersData || [])
      } catch (error) {
        console.error('Error fetching tasks:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const refresh = async () => {
    const [tasksData, usersData] = await Promise.all([
      getDataAPI('user/tasks').catch(() => []),
      getDataAPI('users').catch(() => []),
    ])
    setTasks(tasksData?.data?.data || tasksData?.data || tasksData || [])
    setUsers(usersData || [])
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const response = await postDataAPI('user/tasks', {
        ...formData,
        status: 'pending',
        assigned_to_user_id: formData.assigned_to_user_id || undefined,
        due_date: formData.due_date || undefined,
      })
      
      if (response?.data?.success || response?.success) {
        setIsAddOpen(false)
        setFormData({ title: '', description: '', due_date: '', priority: 'medium', assigned_to_user_id: '', category: 'general' })
        await refresh()
      } else {
        throw new Error(response?.data?.message || response?.message || 'Failed to create task')
      }
    } catch (error) {
      console.error('Failed to create task:', error)
      alert('Failed to create task. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2">Tasks</h1>
          <p className="text-sm text-gray-500">Manage tasks and assignments for your team</p>
        </div>
        <Button onClick={() => setIsAddOpen(true)}>New Task</Button>
      </div>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Create New Task"
        maxWidthClassName="max-w-4xl"
      >
        <form onSubmit={handleCreate} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="ds-input min-h-[90px]"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <DatePicker
                label="Due Date"
                value={formData.due_date}
                onChange={(value) => setFormData({ ...formData, due_date: value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="ds-input"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Related To</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                className="ds-input"
              >
                <option value="general">General</option>
                <option value="shows">Shows</option>
                <option value="music">Music</option>
                <option value="commerce">Commerce</option>
                <option value="finance">Finance</option>
                <option value="tour-manager">Tour Manager</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Assign To</label>
            <select
              value={formData.assigned_to_user_id}
              onChange={(e) => setFormData({ ...formData, assigned_to_user_id: e.target.value })}
              className="ds-input"
            >
              <option value="">Unassigned</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Creating…' : 'Create Task'}
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsAddOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {tasks.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Title</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Description</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Due Date</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Priority</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Assigned To</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tasks.map((task) => {
                const assignee = users.find(u => u.id === task.assigned_to_user_id)
                const isOverdue = task.due_date && (parseLocalDate(task.due_date) || new Date(0)) < new Date()
                return (
                  <tr key={task.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{task.title}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{task.description || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {task.due_date ? (
                        <span className={isOverdue ? 'text-red-600' : ''}>
                          {localDateDisplay(task.due_date)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4">
                      {task.priority && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          task.priority === 'high' ? 'bg-red-100 text-red-800' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.priority}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        task.status === 'completed' ? 'bg-green-50 text-green-700' :
                        task.status === 'pending' ? 'bg-yellow-50 text-yellow-700' :
                        'bg-gray-50 text-gray-700'
                      }`}>
                        {task.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{assignee?.email || '-'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-sm text-gray-500">No tasks found</p>
        </div>
      )}
    </div>
  )
}
